import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Warning,
  CurrencyDollar,
  Stack,
  ArrowsClockwise,
  TrendUp,
  TrendDown,
  Sliders,
} from "@phosphor-icons/react";
import NotificationsCard from "../components/NotificationsCard";
import FeedbackWidget from "../components/FeedbackWidget";

const StatCard = ({ icon: Icon, label, value, sublabel, accent = "bg-[#1A2332]" }) => (
  <div className="bg-white border border-zinc-300 p-4 sm:p-6">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className="font-mono font-bold text-xl sm:text-2xl lg:text-3xl mt-2 sm:mt-3 text-zinc-900 truncate" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          {value}
        </div>
        {sublabel && <div className="text-[10px] sm:text-xs text-zinc-500 mt-1 line-clamp-2">{sublabel}</div>}
      </div>
      <div className={`h-8 w-8 sm:h-10 sm:w-10 ${accent} text-white flex items-center justify-center flex-shrink-0`}>
        <Icon size={16} weight="duotone" className="sm:hidden" />
        <Icon size={20} weight="duotone" className="hidden sm:block" />
      </div>
    </div>
  </div>
);

const txnColor = {
  in: "bg-emerald-600 text-white",
  out: "bg-red-600 text-white",
  adjustment: "bg-amber-400 text-amber-950",
};
const txnIcon = { in: TrendUp, out: TrendDown, adjustment: Sliders };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const showCost = !data?.hide_cost;

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="p-8 text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading…</div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF9800]">
            Control Room · Overview
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mt-1">
            Operations Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-3 font-mono text-[10px] sm:text-xs">
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 uppercase tracking-widest">Last sync</span>
            <span className="text-zinc-900">
              {new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
          <FeedbackWidget variant="inline" />
        </div>
      </div>

      {/* Stats — 2 cols on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-300">
        <div className="contents">
          <div className="bg-zinc-50">
            <StatCard icon={Package} label="Total Parts" value={data.total_parts.toLocaleString()} sublabel={`${data.total_units.toLocaleString()} units in stock`} accent="bg-[#1A2332]" />
          </div>
          <div className="bg-zinc-50">
            {showCost ? (
              <StatCard
                icon={CurrencyDollar}
                label="Inventory Value"
                value={`$${Number(data.inventory_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                sublabel="Stock × unit cost"
                accent="bg-[#FF9800]"
              />
            ) : (
              <StatCard
                icon={Package}
                label="Total Units"
                value={Number(data.total_units).toLocaleString()}
                sublabel="Across all active SKUs"
                accent="bg-[#FF9800]"
              />
            )}
          </div>
          <div className="bg-zinc-50">
            <StatCard
              icon={Warning}
              label="Low Stock"
              value={data.low_stock_count}
              sublabel={`${data.out_of_stock_count} out of stock`}
              accent="bg-amber-400 text-amber-950"
            />
          </div>
          <div className="bg-zinc-50">
            <StatCard
              icon={Stack}
              label="Categories"
              value={data.by_category.length}
              sublabel="Active groupings"
              accent="bg-emerald-600"
            />
          </div>
        </div>
      </div>

      {/* Two column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Low stock */}
        <div className="lg:col-span-2 bg-white border border-zinc-300">
          <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
            <div className="flex items-center gap-2">
              <Warning size={18} weight="duotone" className="text-amber-500" />
              <h2 className="font-display text-lg font-semibold">Low-stock alerts</h2>
            </div>
            <button
              onClick={() => navigate("/parts?low_stock=1")}
              data-testid="view-all-low-stock"
              className="text-[10px] font-bold uppercase tracking-widest text-[#FF9800] hover:text-[#E68900]"
            >
              View all →
            </button>
          </div>
          {data.low_stock.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              All parts above threshold. <span className="text-emerald-600">Healthy.</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900">SKU</th>
                  <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Part</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Stock</th>
                  <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Threshold</th>
                </tr>
              </thead>
              <tbody>
                {data.low_stock.map((p) => (
                  <tr
                    key={p.id}
                    className="row-hover cursor-pointer"
                    onClick={() => navigate(`/parts?focus=${p.id}`)}
                    data-testid={`low-stock-row-${p.sku}`}
                  >
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 font-mono text-xs">{p.sku}</td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-sm">{p.name}</td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono">
                      <span className={p.stock_qty === 0 ? "text-red-600 font-bold" : "text-amber-600 font-bold"}>
                        {p.stock_qty}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono text-zinc-500">
                      {p.low_stock_threshold}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="bg-white border border-zinc-300">
          <div className="border-b border-zinc-300 px-6 py-4">
            <div className="flex items-center gap-2">
              <Stack size={18} weight="duotone" className="text-[#FF9800]" />
              <h2 className="font-display text-lg font-semibold">By category</h2>
            </div>
          </div>
          <div className="px-6 py-4 space-y-4">
            {data.by_category.length === 0 && (
              <div className="text-sm text-zinc-500">No data yet.</div>
            )}
            {data.by_category.map((c) => {
              const max = Math.max(...data.by_category.map((x) => (showCost ? x.value : x.units)), 1);
              const metric = showCost ? c.value : c.units;
              const pct = (metric / max) * 100;
              return (
                <div key={c.category} data-testid={`category-${c.category.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-medium text-zinc-900">{c.category}</span>
                    <span className="font-mono text-zinc-500">
                      {c.units} u
                      {showCost && (
                        <> · ${Number(c.value).toLocaleString(undefined, { maximumFractionDigits: 0 })}</>
                      )}
                    </span>
                  </div>
                  <div className="h-2 bg-zinc-100">
                    <div className="h-full bg-[#FF9800]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notifications */}
      <NotificationsCard />

      {/* Recent transactions */}
      <div className="bg-white border border-zinc-300">
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
          <div className="flex items-center gap-2">
            <ArrowsClockwise size={18} weight="duotone" className="text-[#FF9800]" />
            <h2 className="font-display text-lg font-semibold">Recent activity</h2>
          </div>
          <button
            onClick={() => navigate("/transactions")}
            data-testid="view-all-transactions"
            className="text-[10px] font-bold uppercase tracking-widest text-[#FF9800] hover:text-[#E68900]"
          >
            All transactions →
          </button>
        </div>
        {data.recent_transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Type</th>
                <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Part</th>
                <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900 hidden sm:table-cell">Reason</th>
                <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Qty</th>
                <th className="text-left px-3 sm:px-6 py-3 border-b-2 border-zinc-900 hidden md:table-cell">By</th>
                <th className="text-right px-3 sm:px-6 py-3 border-b-2 border-zinc-900">Time</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions.map((t) => {
                const Icon = txnIcon[t.type];
                return (
                  <tr key={t.id} className="row-hover">
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200">
                      <span className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${txnColor[t.type]}`}>
                        <Icon size={10} weight="bold" /> {t.type}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200">
                      <div className="font-medium text-sm">{t.part_name}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{t.part_sku}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-zinc-600 capitalize hidden sm:table-cell">{t.reason}</td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono font-bold">
                      {t.type === "out" ? "−" : t.type === "in" ? "+" : "="}
                      {t.quantity}
                    </td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-zinc-700 hidden md:table-cell">{t.user_name}</td>
                    <td className="px-3 sm:px-6 py-3 border-b border-zinc-200 text-right font-mono text-[10px] sm:text-xs text-zinc-500 whitespace-nowrap">
                      {new Date(t.created_at).toLocaleString("en-US", {
                        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
